import { useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { getConfig } from '../configs/config'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { loginRequest } from '../authConfig'

export interface PostDataProps {
  url: string
  data: Record<string, unknown> | any
  hasFiles: boolean
  showMessage: boolean
  isAIRequest: boolean
  isStreamRequest: boolean
  type: string
  abortSignal?: AbortSignal
  method?: 'post' | 'put' | 'patch'
  onResponse?: (res: Response) => void
}

export const usePost: any = (app = 'inv') => {
  const { accounts, instance } = useMsal()
  const config = getConfig()
  const [isLoading, setLoader] = useState(false)

  const getScopeForApp = (app: string) => {
    return app === 'nexus' || app === 'semanticdata'
      ? config.REACT_NEXUS_AZURE_SCOPE
      : config.REACT_APP_AZURE_SCOPE
  }

  const request = {
    ...loginRequest,
    scopes: [getScopeForApp(app)],
    account: accounts[0]
  }

  const postData = async ({
    url,
    data = {},
    hasFiles = false,
    isStreamRequest = false,
    abortSignal,
    method = 'post',
    onResponse
  }: PostDataProps) => {
    setLoader(true)
    const msalResponse = await instance.acquireTokenSilent(request).catch((e: any) => {
      if (e instanceof InteractionRequiredAuthError) {
        instance.acquireTokenRedirect(request)
      }
    })

    const options = {
      method: 'post',
      headers: {
        Accept: isStreamRequest ? '*/*' : 'application/json',
        ...(!hasFiles && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${msalResponse?.accessToken}`
      }
    }

    try {
      let endpoint = url
      if (!endpoint.startsWith('https')) {
        const baseUrl =
          app === 'nexus'
            ? config?.REACT_NEXUS_API_URL
            : app === 'semanticdata'
            ? config?.REACT_SEMANTIC_API_URL
            : config?.REACT_APP_API_URL
        endpoint = `${baseUrl}${url}`
      }

      const res = await fetch(endpoint, {
        ...{
          ...options,
          ...(method === 'put' && { method: 'put' }),
          ...(method === 'patch' && { method: 'patch' }),
          ...(abortSignal && { signal: abortSignal })
        },
        body: hasFiles ? data : JSON.stringify(data)
      })
      onResponse?.(res)
      const postResponse = isStreamRequest ? res : await res.json()
      setLoader(false)
      return postResponse
    } catch (error) {
      setLoader(false)
      return error
    }
  }

  return [isLoading, postData] as const
}
